import {
  getCatalogByGroup,
  PERMISSION_GROUPS,
  type PermissionCatalogEntry,
  type PermissionGroup,
  type PermissionGroupKey
} from '@idc-forms/permissions/catalog'
import { FEATURES } from '@/shared/config/features'

/**
 * Qué pestañas del catálogo de permisos se OFRECEN en esta build.
 *
 * ── POR QUÉ EL FILTRO VIVE ACÁ Y NO EN EL CATÁLOGO ───────────────────────────
 *
 * `PERMISSION_GROUPS` es del paquete compartido, y el backend también lo lee. Un paquete que
 * conociera los `VITE_*` de la web ataría el contrato de permisos al bundle de una sola app.
 * El catálogo enumera lo que EXISTE; qué se muestra es decisión del consumidor.
 *
 * ── QUÉ ESTABA ROTO ──────────────────────────────────────────────────────────
 *
 * La pantalla de roles mapeaba `PERMISSION_GROUPS` entero, así que producción ofrecía
 * «Portal de clientes» y «Documentos del cliente» —dos funciones que nadie liberó— mientras
 * otros veinticuatro sitios de la app sí las escondían detrás de su flag.
 *
 * No era cosmético: marcar esos checkboxes CONCEDE permisos que la API honra. `FEATURES`
 * apaga lo que el usuario ve, no lo que el servidor atiende (ver `shared/config/features.ts`),
 * así que la pestaña de más era una puerta para dar acceso real a una función apagada.
 *
 * ── EL MAPA ES EXHAUSTIVO A PROPÓSITO ────────────────────────────────────────
 *
 * `Record<PermissionGroupKey, boolean>` sin `Partial`: agregar un grupo al catálogo ROMPE el
 * typecheck hasta que alguien decida acá si va detrás de un flag. Es lo que faltó la primera
 * vez —el grupo se agregó y nadie se acordó de esta pantalla—, y un olvido que no compila es
 * el único que no se repite.
 */
const GROUP_VISIBILITY: Record<PermissionGroupKey, boolean> = {
  clients: true,
  negotiations: true,
  /**
   * PRENDIDO, y sin flag propio: el módulo ya está entero.
   *
   * Nació en `false` con la condición escrita al lado —«lo prende quien monte el módulo en el
   * menú»— porque los permisos existían desde que se pinnearon los contratos, cuando todavía no
   * había ni pantalla ni endpoints, y una casilla marcable concede permisos REALES sobre una
   * función que nadie liberó.
   *
   * ☠️ Esa condición se cumplió y nadie volvió acá. Créditos tiene listado, expediente, editor de
   * plantilla y sus rutas HTTP con `@V2Policy`, y la semilla reparte siete de los ocho permisos a
   * ADMINISTRACION y COMERCIAL OPERACIONES — pero la pestaña seguía escondida, así que los
   * permisos que la migración reparte **no se podían ni ver ni corregir desde la pantalla de
   * roles**. Es el modo de falla inverso al que el archivo documenta: no una puerta de más, una
   * puerta de menos sobre permisos que ya están concedidos.
   *
   * Entra derecho y no detrás de un `FEATURE_*` porque no hay nada que esconder: el módulo está
   * liberado, y el permiso mismo es la puerta.
   */
  credits: true,
  deliveries: true,
  business: true,
  inventory: true,
  portal: FEATURES.clientPortal,
  /**
   * PRENDIDO, y sin flag: el cartapacio ya no se esconde. Nació detrás de `documentPortfolio`
   * porque había llegado a producción sin que nadie decidiera liberarlo; con la decisión tomada,
   * la pestaña de permisos entra derecha. Lo que sigue gobernado por el lanzamiento de la app son
   * las acciones que alcanzan al cliente (`FEATURES.clientAppLaunched`), no la pantalla de roles.
   */
  documents: true
}

export const isPermissionGroupVisible = (key: PermissionGroupKey): boolean => GROUP_VISIBILITY[key]

/**
 * Los grupos que esta build ofrece, en el orden del catálogo.
 *
 * Se calcula una vez: los `VITE_*` se resuelven en el build y no cambian en runtime.
 */
export const VISIBLE_PERMISSION_GROUPS: readonly PermissionGroup[] = PERMISSION_GROUPS.filter(
  (group) => isPermissionGroupVisible(group.key)
)

/**
 * ☠️ **UN GRUPO VISIBLE PUEDE CONTENER PERMISOS QUE NO LO SON.**
 *
 * El filtro de arriba trabaja por PESTAÑA, y eso alcanza mientras cada función apagada tenga su
 * propio grupo. Si una función vive **dentro** de un grupo que sí se ofrece —como los tres
 * permisos de la app móvil, que viven en «Clientes» como la sección «App móvil»— el filtro de
 * pestañas la deja pasar entera, y para esos casos existe este mapa por permiso.
 *
 * HOY ESTÁ VACÍO a propósito. Los permisos de la app móvil se escondieron aquí detrás de
 * `clientApp` hasta que la función se aprobó para producción; ya no hay ningún permiso atado a un
 * flag de la web. Lo que sigue apagado hasta el lanzamiento son las acciones hacia el cliente, y
 * esas las frena el servidor (`409 CLIENT_APP_NOT_LAUNCHED`), no la pantalla de roles.
 *
 * El mapa y las dos funciones de abajo se quedan porque las pantallas de roles ya los usan (y
 * `visible-permission-groups.spec.ts` fija que lo sigan haciendo): el día que otra función viva
 * dentro de un grupo visible y aún no se libere, se registra acá y las pantallas la esconden sin
 * tocarlas. El motivo sigue en pie: `FEATURES` apaga lo que el usuario VE, no lo que el
 * servidor ATIENDE, y marcar una casilla concede un permiso real.
 */
const PERMISSION_VISIBILITY = new Map<string, boolean>()

/** Un permiso que nadie ató a un flag se muestra: el default es MOSTRAR, como hasta ahora. */
export const isPermissionVisible = (permission: string): boolean =>
  PERMISSION_VISIBILITY.get(permission) ?? true

/**
 * Los permisos de un grupo que esta build ofrece.
 *
 * Reemplaza a `getCatalogByGroup` en las pantallas de roles: el catálogo enumera lo que EXISTE,
 * esto enumera lo que se puede repartir hoy.
 */
export const getVisibleCatalogByGroup = (
  group: PermissionGroupKey
): readonly PermissionCatalogEntry[] =>
  getCatalogByGroup(group).filter((entry) => isPermissionVisible(entry.permission as string))

/**
 * Normaliza el tab que viene de la URL.
 *
 * Sin esto, un `?tab=portal` guardado en favoritos —o el link que alguien pasó cuando el flag
 * estaba prendido— deja la pantalla sin ningún panel visible: `Tabs` no encuentra el trigger
 * y no pinta nada, que se lee como una pantalla rota y no como una función apagada.
 */
export const resolveVisibleTab = (tab: string, fallbackTabs: readonly string[] = []): string => {
  const allowed = [...VISIBLE_PERMISSION_GROUPS.map((group) => group.key), ...fallbackTabs]
  return allowed.includes(tab) ? tab : VISIBLE_PERMISSION_GROUPS[0].key
}
